'use client';

/* eslint-disable max-lines */

import { MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import { Loader2, FileWarning } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { clearHighlights, highlightQuote } from './highlightQuote';
import type { PageExtractedPositions } from '@justisio/shared/server';
import { DocxPreview } from '@/components/workspace/files/DocxPreview';
import { matchQuoteToPositions, mergeCharRects, onlyLetters } from '@/lib/position-highlight-utils';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

interface CitationData {
  id: string;
  content: string;
  pageNumber: number;
  wordBefore?: string;
  wordAfter?: string;
}

interface TRDocumentViewerProps {
  url: string;
  blobUrl?: string;
  mimeType: string;
  fileName: string;
  pdfStorageKey?: string | null;
  initialPage?: number;
  highlightText?: string;
  citationBadgeNumber?: number;
  allCitations?: CitationData[];
  onBadgeClick?: (badgeNumber: number) => void;
  pagesExtractedText?: Record<number, string>;
  pagesExtractedPositions?: Record<number, PageExtractedPositions>;
}

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type ViewerMode = 'pdf' | 'docx' | 'image' | 'unsupported';

function isDocxMimeType(mimeType: string): boolean {
  return mimeType === DOCX_MIME;
}

function getViewerMode(mimeType: string, pdfStorageKey?: string | null): ViewerMode {
  if (mimeType === 'application/pdf') return 'pdf';
  if (isDocxMimeType(mimeType) && pdfStorageKey) return 'pdf';
  if (isDocxMimeType(mimeType)) return 'docx';
  if (mimeType.toLowerCase().startsWith('image/')) return 'image';
  return 'unsupported';
}

function ImageViewer({ url, fileName }: { url: string; fileName: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center overflow-auto bg-muted/30 p-4">
      {/* ponytail: plain img, no zoom/pan — add when image review needs it */}
      {/* eslint-disable-next-line @next/next/no-img-element -- user-uploaded blob/signed-GCS URLs cannot go through the Next image optimizer */}
      <img src={url} alt={fileName} className="max-h-full max-w-full rounded border object-contain" />
    </div>
  );
}

function tryApplyHighlight(pageEl: HTMLElement, quote: string): boolean {
  const textLayer = pageEl.querySelector('.textLayer');
  if (!textLayer) return false;

  const spans = Array.from(textLayer.querySelectorAll('span:not(.markedContent)')) as HTMLElement[];
  if (spans.length === 0) return false;

  return highlightQuote(spans, quote);
}

interface HighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PageEntry {
  badgeNumber: number;
  rects: HighlightRect[];
}

const BADGE_BG = '#eab308';
const HIGHLIGHT_BG = 'rgba(250, 204, 21, 0.35)';
const HIGHLIGHT_OUTLINE = '2px solid rgba(234, 179, 8, 0.7)';

function PositionOverlay({
  entries,
  activeBadgeNumber,
  pagePositions,
  pageWidth,
  leftOffset,
  onBadgeClick,
}: {
  entries: PageEntry[];
  activeBadgeNumber?: number;
  pagePositions: PageExtractedPositions;
  pageWidth: number;
  leftOffset: number;
  onBadgeClick?: (badgeNumber: number) => void;
}) {
  const effectiveScale = pageWidth / pagePositions.pageWidth;
  const TOP_ADJ = 5;
  const PAD = 1;
  const BADGE_COLLISION_THRESHOLD = 14;
  const BADGE_HORIZONTAL_STAGGER = 23;

  // Compute badge positions — one per entry, at the topmost rect's Y
  const badgeSpecs = useMemo(() => {
    const specs: Array<{ badgeNumber: number; top: number; stagger: number }> = [];
    for (const e of entries) {
      let minY = Infinity;
      for (const r of e.rects) {
        const y = (pagePositions.pageHeight - r.y - r.height) * effectiveScale + TOP_ADJ;
        if (y < minY) minY = y;
      }
      specs.push({ badgeNumber: e.badgeNumber, top: Number.isFinite(minY) ? minY + 2 : TOP_ADJ + 2, stagger: 0 });
    }

    specs.sort((a, b) => a.top - b.top);
    for (let i = 1; i < specs.length; i++) {
      if (Math.abs(specs[i].top - specs[i - 1].top) < BADGE_COLLISION_THRESHOLD) {
        specs[i].stagger = specs[i - 1].stagger + 1;
      }
    }
    return specs;
  }, [entries, pagePositions, effectiveScale, TOP_ADJ]);

  return (
    <div className="absolute inset-0" style={{ zIndex: 10 }}>
      {/* Highlight rects — only for the active entry */}
      {entries.map(e => {
        if (e.badgeNumber !== activeBadgeNumber) return null;
        return e.rects.map((r, idx) => (
          <div
            key={idx}
            className="td-pos-highlight"
            onClick={() => onBadgeClick?.(e.badgeNumber)}
            style={{
              position: 'absolute',
              left: r.x * effectiveScale + leftOffset - PAD,
              top: (pagePositions.pageHeight - r.y - r.height) * effectiveScale + TOP_ADJ - PAD,
              width: r.width * effectiveScale + PAD * 2,
              height: r.height * effectiveScale + PAD * 2,
              backgroundColor: HIGHLIGHT_BG,
              outline: HIGHLIGHT_OUTLINE,
              outlineOffset: -1,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          />
        ));
      })}

      {/* Badges for ALL entries */}
      {badgeSpecs.map(s => {
        const isActive = s.badgeNumber === activeBadgeNumber;
        return (
          <div
            key={s.badgeNumber}
            className="td-pos-badge"
            onClick={() => onBadgeClick?.(s.badgeNumber)}
            style={{
              position: 'absolute',
              left: 6 - s.stagger * BADGE_HORIZONTAL_STAGGER,
              top: s.top,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: BADGE_BG,
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
              zIndex: 15,
              opacity: isActive ? 1 : 0.55,
            }}
          >
            {s.badgeNumber}
          </div>
        );
      })}
    </div>
  );
}

/**
 * findQuoteYInTextLayer searches the react-pdf text layer for the given quote
 * and returns its Y offset relative to the page container, or null if not found.
 */
function findQuoteYInTextLayer(container: HTMLElement, quote: string): number | null {
  const textLayer = container.querySelector('.textLayer');
  if (!textLayer) return null;

  const spans = Array.from(textLayer.querySelectorAll('span:not(.markedContent)')) as HTMLElement[];
  if (spans.length === 0) return null;

  const strippedQuote = onlyLetters(quote);
  let accumulated = '';
  const spanStarts: number[] = [];

  for (const span of spans) {
    spanStarts.push(accumulated.length);
    accumulated += onlyLetters(span.textContent ?? '');
  }

  const matchPos = accumulated.indexOf(strippedQuote);
  if (matchPos === -1) return null;

  // Find which span contains the match start
  const spanIdx = spanStarts.reduce((best, start, i) => start <= matchPos ? i : best, 0);

  const rect = spans[spanIdx].getBoundingClientRect();
  const pageEl = container.querySelector('.react-pdf__Page');
  if (!pageEl) return rect.top;
  const pageRect = pageEl.getBoundingClientRect();
  return rect.top - pageRect.top;
}

// eslint-disable-next-line max-lines-per-function
function LazyPdfPage({
  pageNumber,
  pageWidth,
  estHeight,
  isInitialPage,
  entries: pageEntries,
  activeBadgeNumber,
  pagePositions,
  fallbackCitations,
  onBadgeClick,
}: {
  pageNumber: number;
  pageWidth: number;
  estHeight: number;
  isInitialPage: boolean;
  entries: PageEntry[];
  activeBadgeNumber?: number;
  pagePositions?: PageExtractedPositions | null;
  fallbackCitations?: Array<{ badgeNumber: number; quote: string }>;
  onBadgeClick?: (badgeNumber: number) => void;
}) {
  const [hasRendered, setHasRendered] = useState(isInitialPage);
  const [isLoaded, setIsLoaded] = useState(false);
  const [badgeYMap, setBadgeYMap] = useState<Map<number, number>>(new Map());
  const [leftOffset, setLeftOffset] = useState(24);
  const ref = useRef<HTMLDivElement>(null);

  // Measure the page element's left edge offset so highlight rects align with
  // the canvas regardless of container padding or PDF CropBox (same approach
  // as Tinjau Dokumen's DocumentReviewPositionHighlights).
  useEffect(() => {
    if (!ref.current) return;
    const outerContainer = ref.current.firstElementChild;
    if (!outerContainer) return;
    const pageEl = outerContainer.querySelector('.react-pdf__Page');
    if (!pageEl) return;
    const parentRect = outerContainer.getBoundingClientRect();
    const pageRect = pageEl.getBoundingClientRect();
    setLeftOffset(pageRect.left - parentRect.left);
  }, [isLoaded]);

  useEffect(() => {
    if (hasRendered) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          setHasRendered(true);
          observer.disconnect();
        }
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasRendered]);

  const shouldRender = hasRendered || isInitialPage;
  const hasPositions = pageEntries.length > 0 && pagePositions != null;

  // Measure Y positions for each fallback citation independently
  useEffect(() => {
    if (!isLoaded) return;
    if (hasPositions) return;
    if (!fallbackCitations?.length) return;

    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const textLayer = el.querySelector('.textLayer');
      if (!textLayer || !textLayer.querySelector('span')) return false;

      const next = new Map(badgeYMap);
      let changed = false;
      for (const fc of fallbackCitations) {
        if (next.has(fc.badgeNumber)) continue;
        const y = findQuoteYInTextLayer(el, fc.quote);
        if (y != null) {
          next.set(fc.badgeNumber, y);
          changed = true;
        }
      }
      if (changed) setBadgeYMap(next);
      return next.size >= fallbackCitations.length;
    };

    if (!measure()) {
      const observer = new MutationObserver(() => {
        if (measure()) observer.disconnect();
      });
      observer.observe(el, { childList: true, subtree: true });
      return () => observer.disconnect();
    }
  }, [isLoaded, hasPositions, fallbackCitations, badgeYMap]);

  return (
    <div
      ref={ref}
      id={`doc-page-${pageNumber}`}
      className="mb-2 flex justify-center"
      style={{
        minHeight: isLoaded ? undefined : estHeight,
        width: pageWidth,
      }}
    >
      {shouldRender && (
        <div className="relative">
          <Page
            pageNumber={pageNumber}
            width={pageWidth}
            className="shadow-sm bg-white"
            renderTextLayer
            renderAnnotationLayer
            onLoadSuccess={() => setIsLoaded(true)}
          />
          {hasPositions && (
            <PositionOverlay
              entries={pageEntries}
              activeBadgeNumber={activeBadgeNumber}
              pagePositions={pagePositions}
              pageWidth={pageWidth}
              leftOffset={leftOffset}
              onBadgeClick={onBadgeClick}
            />
          )}
          {!hasPositions && (fallbackCitations ?? []).map((fc, idx) => {
            const isActive = fc.badgeNumber === activeBadgeNumber;
            const y = badgeYMap.get(fc.badgeNumber) ?? 8 + idx * 24;
            return (
              <div
                key={fc.badgeNumber}
                className="td-pos-badge"
                onClick={() => onBadgeClick?.(fc.badgeNumber)}
                style={{
                  position: 'absolute',
                  left: 6,
                  top: y,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: BADGE_BG,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1,
                  zIndex: 15,
                  opacity: isActive ? 1 : 0.55,
                }}
              >
                {fc.badgeNumber}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface PageCitationEntry {
  badgeNumber: number;
  quote: string;
  wordBefore?: string;
  wordAfter?: string;
}

interface PdfViewerProps {
  url: string;
  initialPage?: number;
  highlightText?: string;
  citationBadgeNumber?: number;
  allCitations?: CitationData[];
  onBadgeClick?: (badgeNumber: number) => void;
  pagesExtractedText?: Record<number, string>;
  pagesExtractedPositions?: Record<number, PageExtractedPositions>;
}

// eslint-disable-next-line max-lines-per-function
function PdfViewer({
  url,
  initialPage,
  highlightText,
  citationBadgeNumber,
  allCitations,
  onBadgeClick,
  pagesExtractedText,
  pagesExtractedPositions,
}: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [zoom, setZoom] = useState(100);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [aspectRatio, setAspectRatio] = useState<number>(1.414);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width - 32);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!initialPage || !containerRef.current) return;
    const target = containerRef.current.querySelector(`#doc-page-${initialPage}`);
    if (target) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [initialPage, numPages]);

  // Group citations by page number
  const citationsByPage = useMemo(() => {
    const map = new Map<number, PageCitationEntry[]>();
    (allCitations ?? []).forEach((cit, idx) => {
      const num = cit.pageNumber;
      if (!map.has(num)) map.set(num, []);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      map.get(num)!.push({
        badgeNumber: idx + 1,
        quote: cit.content,
        wordBefore: cit.wordBefore,
        wordAfter: cit.wordAfter,
      });
    });
    return map;
  }, [allCitations]);

  // Compute rects for ALL citations per page (position-based)
  const allPageEntries = useMemo(() => {
    const result: Record<number, PageEntry[]> = {};
    const pageCits = citationsByPage;

    for (const [pageNum, entries] of pageCits) {
      const pagePositions = pagesExtractedPositions?.[pageNum];
      const pageText = pagesExtractedText?.[pageNum];
      if (!pagePositions || !pageText) continue;

      const pageEntryList: PageEntry[] = [];
      for (const e of entries) {
        const charRects = matchQuoteToPositions(e.quote, pageText, pagePositions.chars, {
          wordBefore: e.wordBefore,
          wordAfter: e.wordAfter,
        });
        if (charRects.length === 0) continue;
        const merged = mergeCharRects(charRects);
        // Collapse vertically overlapping rects. In PDF coords y increases
        // upward, so rect A overlaps rect B if A.y - A.height < B.y (A's
        // bottom edge extends above B's top edge).
        for (let i = 1; i < merged.length; i++) {
          const prev = merged[i - 1];
          const curr = merged[i];
          const prevBottom = prev.y - prev.height;
          const currBottom = curr.y - curr.height;
          if (prevBottom < curr.y) {
            // Merge: extend prev to cover curr
            const newY = Math.max(prev.y, curr.y);
            const newBottom = Math.min(prevBottom, currBottom);
            prev.y = newY;
            prev.height = newY - newBottom;
            merged.splice(i, 1);
            i--;
          }
        }
        pageEntryList.push({ badgeNumber: e.badgeNumber, rects: merged });
      }

      if (pageEntryList.length > 0) {
        result[pageNum] = pageEntryList;
      }
    }
    return result;
  }, [citationsByPage, pagesExtractedText, pagesExtractedPositions]);

  // Text-layer fallback when positions are unavailable
  useEffect(() => {
    if (!containerRef.current) return;
    if (allPageEntries[initialPage ?? -1]?.length) return; // Position highlights take priority

    containerRef.current.querySelectorAll('.textLayer').forEach((tl) => {
      clearHighlights(Array.from(tl.querySelectorAll('span:not(.markedContent)')) as HTMLElement[]);
    });

    if (!highlightText || !initialPage) return;

    const pageEl = containerRef.current.querySelector(`#doc-page-${initialPage}`);
    if (!pageEl) return;

    const applyAndScroll = (el: HTMLElement) => {
      if (tryApplyHighlight(el, highlightText)) {
        const highlightEl = el.querySelector('.pdf-text-highlight');
        if (highlightEl) {
          highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return true;
      }
      return false;
    };

    if (applyAndScroll(pageEl as HTMLElement)) return;

    const observer = new MutationObserver(() => {
      if (applyAndScroll(pageEl as HTMLElement)) {
        observer.disconnect();
      }
    });
    observer.observe(pageEl, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [highlightText, initialPage, numPages, zoom, allPageEntries]);

  return (
    <div className="flex flex-col h-full relative">
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 px-2 py-3">
        <Document
          file={url}
          onLoadSuccess={async (pdf) => {
            setNumPages(pdf.numPages);
            try {
              const page = await pdf.getPage(1);
              const viewport = page.getViewport({ scale: 1 });
              if (viewport.width && viewport.height) {
                setAspectRatio(viewport.height / viewport.width);
              }
            } catch (e) {
              console.error('Failed to calculate aspect ratio', e);
            }
          }}
          loading={
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, i) => {
            const pageWidth = containerWidth * (zoom / 100);
            const estHeight = pageWidth * aspectRatio;
            const pageNum = i + 1;
            const pageEntries = allPageEntries[pageNum] ?? [];
            const pageCits = citationsByPage.get(pageNum);

            // For fallback (no position data): pass citation quotes for Y measurement
            const fallbackCitations = !pageEntries.length && pageCits
              ? pageCits.map(e => ({ badgeNumber: e.badgeNumber, quote: e.quote }))
              : undefined;

            return (
              <LazyPdfPage
                key={i}
                pageNumber={pageNum}
                pageWidth={pageWidth}
                estHeight={estHeight}
                isInitialPage={pageNum === initialPage}
                entries={pageEntries}
                activeBadgeNumber={citationBadgeNumber}
                pagePositions={pagesExtractedPositions?.[pageNum]}
                fallbackCitations={fallbackCitations}
                onBadgeClick={onBadgeClick}
              />
            );
          })}
        </Document>
      </div>
      {numPages > 0 && (
        <div className="absolute top-2 right-2 z-30 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 px-2 py-1">
          <button
            onClick={() => setZoom((z) => Math.max(50, z - 10))}
            disabled={zoom <= 50}
            className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 transition-colors text-gray-600"
          >
            <MinusIcon className="h-3 w-3" />
          </button>
          <span className="text-xs text-gray-500 tabular-nums w-8 text-center select-none">
            {zoom}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.min(300, z + 10))}
            disabled={zoom >= 300}
            className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 transition-colors text-gray-600"
          >
            <PlusIcon className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function DocxViewer({ url, fileName }: { url: string; fileName: string }) {
  return <DocxPreview url={url} fileName={fileName} />;
}

function UnsupportedViewer({ fileName }: { fileName: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 px-4">
      <FileWarning className="h-6 w-6" />
      <p className="text-xs text-center">
        Pratinjau tidak tersedia untuk tipe file ini.
      </p>
      <p className="text-xs text-gray-300 truncate max-w-full" title={fileName}>
        {fileName}
      </p>
    </div>
  );
}

export function TRDocumentViewer({
  url,
  blobUrl,
  mimeType,
  fileName,
  pdfStorageKey,
  initialPage,
  highlightText,
  citationBadgeNumber,
  allCitations,
  onBadgeClick,
  pagesExtractedText,
  pagesExtractedPositions,
}: TRDocumentViewerProps) {
  const mode = getViewerMode(mimeType, pdfStorageKey);
  const isConvertedDocx = isDocxMimeType(mimeType) && pdfStorageKey;
  const sourceUrl = isConvertedDocx ? url : (blobUrl ?? url);

  switch (mode) {
    case 'pdf':
      return (
        <PdfViewer
          url={sourceUrl}
          initialPage={initialPage}
          highlightText={highlightText}
          citationBadgeNumber={citationBadgeNumber}
          allCitations={allCitations}
          onBadgeClick={onBadgeClick}
          pagesExtractedText={pagesExtractedText}
          pagesExtractedPositions={pagesExtractedPositions}
        />
      );
    case 'docx':
      return <DocxViewer url={sourceUrl} fileName={fileName} />;
    case 'image':
      return <ImageViewer url={sourceUrl} fileName={fileName} />;
    default:
      return <UnsupportedViewer fileName={fileName} />;
  }
}
